import { Link } from 'components/router';
import { useSetSourceContext } from 'lib/business/source-context';
import { kebabCase } from 'lodash-es';
import { useIntl } from 'react-intl';

import Divider from 'components/divider';
import SkipLink from 'components/skip-link';
import { Heading, Text } from 'components/typography';

import { TopicsGroupData } from 'pages/topics';

const HASH = '#';

type TopicsLetterGroupProps = {
  data: TopicsGroupData;
  isLastGroup?: boolean;
  nextGroupLetter?: string;
};

export default function TopicsGroup({
  data,
  isLastGroup,
  nextGroupLetter
}: TopicsLetterGroupProps): React.ReactNode {
  const setContentDiscoveryContext = useSetSourceContext(
    'content_discovery_context'
  );
  const intl = useIntl();
  const sectionLetter = data.letter;
  const skipText =
    nextGroupLetter === HASH
      ? intl.formatMessage({ defaultMessage: 'Skip to other topics' })
      : intl.formatMessage(
          {
            defaultMessage: 'Skip to letter {nextGroupLetter}'
          },
          { nextGroupLetter }
        );
  const groupAriaText =
    sectionLetter === HASH
      ? intl.formatMessage({ defaultMessage: 'Other topics group' })
      : intl.formatMessage(
          {
            defaultMessage: 'Topics starting with a letter {sectionLetter}'
          },
          { sectionLetter }
        );

  // Filter out any items with empty names as a safety measure
  const validItems = data.items.filter(item => item.name && item.name.trim());

  return (
    <div
      id={sectionLetter.toLowerCase()}
      aria-label={groupAriaText}
      role="group"
      tabIndex={-1}
    >
      <div className="py-10 md:py-12">
        <div className="mb-4 flex items-end md:mb-6 xl:mb-10">
          <Heading size="l" aria-hidden tagName="h2">
            {sectionLetter}
          </Heading>
          {nextGroupLetter && (
            <SkipLink to={`#${nextGroupLetter.toLowerCase()}`} className="ml-4">
              {skipText}
            </SkipLink>
          )}
        </div>
        <ul className="column-count-2 md:column-count-3 lg:column-count-4 xl:column-count-6 h-max pb-3">
          {validItems.map(item => {
            const displayName =
              item.name.charAt(0).toUpperCase() + item.name.slice(1);
            return (
              <li key={item.slug} className="inline-block w-full py-2">
                <Link
                  onClick={() => {
                    setContentDiscoveryContext(
                      `topics-${kebabCase(item.slug)}`
                    );
                  }}
                  href={`/topics/${item.slug}`}
                  prefetch={false}
                  className="mb-1 block font-bold hover:underline"
                >
                  <Text size="m" className="xl:text-base xl:leading-md">
                    {displayName}
                  </Text>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
      {!isLastGroup && <Divider isLarge />}
    </div>
  );
}
